import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { FC, ReactNode, useRef } from 'react';
import ReactDOM from 'react-dom';

import BackOverlay from '@/components/core/modal/BackOverlay';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { useRootElement } from '@/hooks/useRootElement';

import cn from './style.module.sass';

type ModalPlacement = 'center' | 'top';

export interface IModal {
	title?: string;
	trigger?: ReactNode;
	isOpen: boolean;
	handleClose?: () => void;
	handleOpen?: () => void;
	noToggles?: boolean;
	className?: string;
	bgClassName?: string;
	wrapperClassName?: string;
	outerClassname?: string;
	clickOutsideExceptionsId?: string[];
	hasCross?: boolean;
	disableCloseOutside?: boolean;
	placement?: ModalPlacement;
	overlayId?: string;
}

interface IProps {
	isOpen: boolean;
	closeModal: () => void;
	children: ReactNode;
	className?: string;
	bgClassName?: string;
	wrapperClassName?: string;
	outerClassname?: string;
	clickOutsideExceptionsId?: string[];
	hasCross?: boolean;
	disableCloseOutside?: boolean;
	placement?: ModalPlacement;
	overlayId?: string;
}

const ModalInner: FC<IProps> = ({
	children,
	isOpen,
	closeModal,
	className,
	bgClassName,
	wrapperClassName,
	outerClassname,
	clickOutsideExceptionsId,
	hasCross = true,
	disableCloseOutside = false,
	placement = 'center',
	overlayId,
}) => {
	const modalRef = useRef(null);	
	useOnClickOutside(modalRef, disableCloseOutside ? undefined : closeModal, clickOutsideExceptionsId);
	
	if (!isOpen) return null;

	return (
		<>
			<div className={clsx(cn.wrapper, wrapperClassName)}>
				<table className={clsx(cn.outer, placement === 'top' && cn.topOuter, outerClassname)}>
					<tbody>
						<tr>
							<td>
								<div className={clsx(cn.inner, className)} ref={modalRef}>
									{hasCross && (
										<div
											className={clsx(cn.crossIcon, 'crossIcon')}
											onClick={closeModal}
											role="presentation"
										>
											<span className={clsx(cn.cross, 'cross')} />
										</div>
									)}
									<div className={cn.content}>{children}</div>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<BackOverlay showOverlay={isOpen} closeOverlay={closeModal} className={bgClassName} id={overlayId} />
		</>
	);
};

const Modal: FC<IModal> = observer((props) => {
	const { trigger, isOpen, handleClose, handleOpen, children, disableCloseOutside } = props;

	const node = useRootElement('body');

	return (
		<>
			{trigger && (
				<div onClick={handleOpen} role="presentation">
					{trigger}
				</div>
			)}
			{node &&
				ReactDOM.createPortal(
					<ModalInner {...props} isOpen={isOpen} closeModal={handleClose} disableCloseOutside={disableCloseOutside}>
						{children}
					</ModalInner>,
					node
				)}
		</>
	);
});

export default Modal;
